import { Box, HStack, IconButton, Stack, VStack } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

import { CV_LIMIT, UserCV } from '../types';
import { createDefaultSettings } from '../utils/cvDefaults';
import { buildCVShareUrl } from '../utils/cvShare';

interface CVCardProps {
  cv: UserCV;
  totalCVs: number;
}

const TEMPLATE_LABELS: Record<string, string> = {
  classic: 'Classic',
  modern: 'Modern',
  creative: 'Creative',
};

const CVCard = ({ cv, totalCVs }: CVCardProps) => {
  const navigate = useNavigate();
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToastWrapper();
  const { id: currentUserId } = useCurrentUser();
  const { isMobile } = useMediaQueries();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('user_cv').delete().eq('id', cv.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCVs, currentUserId] });
      toastSuccess('CV deleted');
    },
    onError: () => {
      toastError('Could not delete CV');
    },
  });

  const duplicateMutation = useMutation<{ cvId?: string }, string>({
    mutationFn: supabaseMutationFn(() =>
      supabase.functions.invoke<{ cvId?: string }>(EdgeFunctionName.RedeemTokenReward, {
        body: { rewardType: 'duplicate-cv', sourceCvId: cv.id },
      })
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCVs, currentUserId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, currentUserId] });
      toastSuccess('CV duplicated (1 BigToken)');
    },
    onError: (error) => {
      toastError(error || 'Could not duplicate CV');
    },
  });

  const accentColor = cv.settings?.accentColor || createDefaultSettings().accentColor;
  const jobTitle = cv.data?.personalInfo?.jobTitle || 'No role specified';
  const fullName = cv.data?.personalInfo?.fullName || '';
  const sectionCount = cv.data?.sections?.length || 0;
  const updatedAt = new Date(cv.updated_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const canDuplicate = totalCVs < CV_LIMIT;

  return (
    <Box
      backgroundColor="whiteAlpha.50"
      borderRadius="lg"
      border="1px solid"
      borderColor="whiteAlpha.100"
      overflow="hidden"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ borderColor: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
      onClick={() => navigate(`/cv-builder/${cv.id}`)}
    >
      {/* Template color accent bar */}
      <Box height="4px" background={accentColor} />

      {/* Preview area */}
      <Box px={5} pt={5} pb={3}>
        {/* Template badge */}
        <HStack justifyContent="space-between" mb={3}>
          <Box
            px={2}
            py={0.5}
            borderRadius="full"
            backgroundColor="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <Text fontSize="xMicro" fontWeight="semibold" opacity="secondary">
              {TEMPLATE_LABELS[cv.template] || 'Classic'}
            </Text>
          </Box>
          {cv.is_copy && (
            <Box px={2} py={0.5} borderRadius="full" backgroundColor="whiteAlpha.100">
              <Text fontSize="xMicro" opacity="tertiary">
                Duplicate
              </Text>
            </Box>
          )}
        </HStack>

        {/* CV mini preview */}
        <Box
          backgroundColor="white"
          borderRadius="md"
          p={4}
          minHeight={isMobile ? '120px' : '160px'}
          position="relative"
          overflow="hidden"
        >
          <Box height="3px" width="60%" backgroundColor={accentColor} borderRadius="full" mb={2} />
          <Box height="8px" width="80%" backgroundColor="gray.300" borderRadius="sm" mb={1} />
          <Box height="6px" width="50%" backgroundColor="gray.200" borderRadius="sm" mb={3} />
          {Array.from({ length: Math.min(sectionCount, 3) }).map((_, i) => (
            <Stack key={i} spacing={1} mb={2}>
              <Box height="5px" width="40%" backgroundColor="gray.300" borderRadius="sm" />
              <Box height="4px" width="90%" backgroundColor="gray.100" borderRadius="sm" />
              <Box height="4px" width="75%" backgroundColor="gray.100" borderRadius="sm" />
            </Stack>
          ))}
        </Box>

        {/* CV info */}
        <VStack alignItems="flex-start" spacing={1} mt={3}>
          <Text fontSize="medium" fontWeight="semibold" noOfLines={1}>
            {cv.title}
          </Text>
          <HStack spacing={2}>
            {fullName && (
              <Text fontSize="small" opacity="secondary" noOfLines={1}>
                {fullName}
              </Text>
            )}
            <Text fontSize="small" opacity="tertiary">
              {jobTitle}
            </Text>
          </HStack>
        </VStack>
      </Box>

      {/* Footer with actions */}
      <HStack
        px={5}
        py={3}
        borderTop="1px solid"
        borderColor="whiteAlpha.100"
        justifyContent="space-between"
        onClick={(e) => e.stopPropagation()}
      >
        <Text fontSize="micro" opacity="tertiary">
          {updatedAt}
        </Text>
        <HStack spacing={1}>
          {cv.is_shared && (
            <>
              <DevSandboxTooltip label="Copy shareable URL">
                <IconButton
                  aria-label="Copy shareable URL"
                  icon={<Icon type="Share" size={16} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(buildCVShareUrl(cv.id))
                      .then(() => toastSuccess('Shareable CV URL copied'))
                      .catch(() => undefined);
                  }}
                />
              </DevSandboxTooltip>
            </>
          )}
          {cv.ai_report && (
            <DevSandboxTooltip label="View AI Review">
              <IconButton
                aria-label="View AI Review"
                icon={<Icon type="GenerateCodeSolution" size={16} />}
                size="sm"
                variant="ghost"
                color="green.400"
                onClick={() => navigate(`/cv-builder/${cv.id}?ai-review=true`)}
              />
            </DevSandboxTooltip>
          )}
          <DevSandboxTooltip label={canDuplicate ? 'Duplicate (1 BigToken)' : `CV limit reached (${CV_LIMIT}/${CV_LIMIT})`}>
            <IconButton
              aria-label="Duplicate CV"
              icon={<Icon type="Copy" size={16} />}
              size="sm"
              variant="ghost"
              isDisabled={!canDuplicate || duplicateMutation.isPending}
              isLoading={duplicateMutation.isPending}
              onClick={() => duplicateMutation.mutate()}
            />
          </DevSandboxTooltip>
          <DevSandboxTooltip label="Delete">
            <IconButton
              aria-label="Delete CV"
              icon={<Icon type="Trash" size={16} />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            />
          </DevSandboxTooltip>
        </HStack>
      </HStack>
    </Box>
  );
};

export default CVCard;
